import type { IconType } from 'react-icons'
import {
  FaDiscord,
  FaFacebook,
  FaGithub,
  FaInstagram,
  FaLinkedin,
  FaTelegram,
  FaTiktok,
  FaWhatsapp,
  FaXTwitter,
  FaYoutube,
} from 'react-icons/fa6'

export const SOCIAL_PLATFORM_IDS = [
  'facebook',
  'linkedin',
  'instagram',
  'twitter',
  'youtube',
  'tiktok',
  'github',
  'discord',
  'telegram',
  'whatsapp',
] as const

export type SocialPlatformId = (typeof SOCIAL_PLATFORM_IDS)[number]

export interface SocialPlatformDefinition {
  id: SocialPlatformId
  label: string
  hosts: string[]
  icon: IconType
}

export const SOCIAL_PLATFORMS: SocialPlatformDefinition[] = [
  {
    id: 'facebook',
    label: 'Facebook',
    hosts: ['facebook.com', 'fb.com', 'm.facebook.com', 'www.facebook.com'],
    icon: FaFacebook,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    hosts: ['linkedin.com', 'www.linkedin.com'],
    icon: FaLinkedin,
  },
  {
    id: 'instagram',
    label: 'Instagram',
    hosts: ['instagram.com', 'www.instagram.com'],
    icon: FaInstagram,
  },
  {
    id: 'twitter',
    label: 'X (Twitter)',
    hosts: ['twitter.com', 'x.com', 'www.twitter.com', 'www.x.com'],
    icon: FaXTwitter,
  },
  {
    id: 'youtube',
    label: 'YouTube',
    hosts: ['youtube.com', 'youtu.be', 'www.youtube.com'],
    icon: FaYoutube,
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    hosts: ['tiktok.com', 'www.tiktok.com'],
    icon: FaTiktok,
  },
  {
    id: 'github',
    label: 'GitHub',
    hosts: ['github.com', 'www.github.com'],
    icon: FaGithub,
  },
  {
    id: 'discord',
    label: 'Discord',
    hosts: ['discord.com', 'discord.gg', 'www.discord.com'],
    icon: FaDiscord,
  },
  {
    id: 'telegram',
    label: 'Telegram',
    hosts: ['t.me', 'telegram.me', 'telegram.org', 'www.telegram.org'],
    icon: FaTelegram,
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    hosts: ['wa.me', 'whatsapp.com', 'api.whatsapp.com', 'www.whatsapp.com'],
    icon: FaWhatsapp,
  },
]

const platformById = new Map(SOCIAL_PLATFORMS.map(platform => [platform.id, platform]))

export function getSocialPlatform(id: string): SocialPlatformDefinition | undefined {
  return platformById.get(id as SocialPlatformId)
}

export function isSocialPlatformId(value: string): value is SocialPlatformId {
  return SOCIAL_PLATFORM_IDS.includes(value as SocialPlatformId)
}

export function normalizeSocialPlatformHost(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, '')
}

export function urlMatchesSocialPlatform(url: string, platformId: SocialPlatformId): boolean {
  try {
    const host = normalizeSocialPlatformHost(new URL(url.trim()).hostname)
    const platform = getSocialPlatform(platformId)
    if (!platform) {
      return false
    }
    return platform.hosts.some(candidate => {
      const normalized = normalizeSocialPlatformHost(candidate)
      return host === normalized || host.endsWith(`.${normalized}`)
    })
  } catch {
    return false
  }
}

export function inferSocialPlatformFromUrl(url: string): SocialPlatformId | null {
  for (const platform of SOCIAL_PLATFORMS) {
    if (urlMatchesSocialPlatform(url, platform.id)) {
      return platform.id
    }
  }
  return null
}

export function inferSocialPlatformFromLabel(label: string): SocialPlatformId | null {
  const normalized = label.trim().toLowerCase()
  const match = SOCIAL_PLATFORMS.find(
    platform =>
      platform.id === normalized ||
      platform.label.toLowerCase() === normalized ||
      platform.label.toLowerCase().replace(/\s*\(.*\)\s*/, '') === normalized
  )
  return match?.id ?? null
}

export function getSocialPlatformLabel(platformId: SocialPlatformId): string {
  return getSocialPlatform(platformId)?.label ?? platformId
}
